// MedicinePicker.tsx — search-and-pick an existing medicines row, or
// "Declare new brand" to create one. Shared by the Goods Receipt and
// Purchase Order line editors (and, later, IP medication ordering) so a
// product is always resolved through one explicit action, never an
// implicit server-side guess. See
// docs/HMS_v4_Drug_Master_Formulary_Architecture.md §20-§21.
import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { searchMedicines, createMedicine } from './api';
import type { MedicineSearchResult } from './types';

const emptyNewBrand = { generic_name: '', brand_name: '', strength: '', dosage_form: 'Tablet', hsn_code: '', manufacturer_name: '' };

export default function MedicinePicker({ medicineId, label, onResolved, onClear }: {
  medicineId: number | null;
  /** What to show once resolved, e.g. "Paracetamol — Dolo 500mg". */
  label: string;
  onResolved: (result: MedicineSearchResult) => void;
  onClear: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MedicineSearchResult[]>([]);
  const [declaring, setDeclaring] = useState(false);
  const [newBrand, setNewBrand] = useState(emptyNewBrand);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!query.trim() || medicineId) { setResults([]); return; }
    const t = setTimeout(() => { searchMedicines(query).then(setResults).catch(() => setResults([])); }, 250);
    return () => clearTimeout(t);
  }, [query, medicineId]);

  const pick = (r: MedicineSearchResult) => {
    onResolved(r);
    setQuery('');
    setResults([]);
  };

  const declareNewBrand = async () => {
    if (!newBrand.generic_name.trim()) { setError('Generic name is required'); return; }
    setCreating(true); setError('');
    try {
      const created = await createMedicine(newBrand);
      onResolved(created);
      setDeclaring(false);
      setNewBrand(emptyNewBrand);
    } catch (e: any) { setError(e.message); }
    setCreating(false);
  };

  if (medicineId) {
    return (
      <div className="flex items-center gap-1.5 rounded-[var(--radius)] border border-line bg-canvas px-2 py-1.5">
        <span className="flex-1 truncate text-xs">✓ {label}</span>
        <button type="button" className="text-[10px] text-ink-muted underline" onClick={onClear}>change</button>
      </div>
    );
  }

  if (declaring) {
    return (
      <div className="rounded-[var(--radius)] border border-line bg-canvas p-2">
        {error && <div className="mb-1.5 text-[10.5px] text-danger-ink">{error}</div>}
        <div className="mb-1.5 grid grid-cols-3 gap-1.5">
          <Input placeholder="Generic name *" value={newBrand.generic_name} onChange={(e) => setNewBrand((b) => ({ ...b, generic_name: e.target.value }))} className="text-xs" />
          <Input placeholder="Brand" value={newBrand.brand_name} onChange={(e) => setNewBrand((b) => ({ ...b, brand_name: e.target.value }))} className="text-xs" />
          <Input placeholder="Strength" value={newBrand.strength} onChange={(e) => setNewBrand((b) => ({ ...b, strength: e.target.value }))} className="text-xs" />
        </div>
        <div className="mb-1.5 grid grid-cols-3 gap-1.5">
          <Input placeholder="Dosage form" value={newBrand.dosage_form} onChange={(e) => setNewBrand((b) => ({ ...b, dosage_form: e.target.value }))} className="text-xs" />
          <Input placeholder="HSN code" value={newBrand.hsn_code} onChange={(e) => setNewBrand((b) => ({ ...b, hsn_code: e.target.value }))} className="text-xs" />
          <Input placeholder="Manufacturer" value={newBrand.manufacturer_name} onChange={(e) => setNewBrand((b) => ({ ...b, manufacturer_name: e.target.value }))} className="text-xs" />
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" disabled={creating} onClick={declareNewBrand}>{creating ? 'Creating…' : 'Create product'}</Button>
          <Button variant="ghost" size="sm" onClick={() => { setDeclaring(false); setError(''); }}>Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <Input placeholder="Search generic or brand…" value={query} onChange={(e) => setQuery(e.target.value)} className="text-xs" />
      {results.length > 0 && (
        <div className="absolute z-10 mt-1 max-h-40 w-full overflow-y-auto rounded-[var(--radius)] border border-line bg-surface shadow-md">
          {results.map((r) => (
            <button key={r.id} type="button" className="block w-full px-2 py-1 text-left text-xs hover:bg-canvas" onClick={() => pick(r)}>
              {r.generic_name}{r.brand_name ? ` — ${r.brand_name}` : ''}{r.strength ? ` ${r.strength}` : ''}
            </button>
          ))}
        </div>
      )}
      <button type="button" className="mt-1 text-[10px] text-ink-muted underline" onClick={() => setDeclaring(true)}>+ Declare new brand</button>
    </div>
  );
}
